import { Text, Tooltip, TooltipContent, TooltipTrigger } from '@/ui-kit'
import { Spinner } from '@/ui-kit/components/Spinner'
import { Plural, Trans, useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { ConnectionState } from 'livekit-client'
import { ChevronDown, Mic, MicOff, PhoneOff, RefreshCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { leaveSession } from '../../api/conversations-api'
import { useConversationLogger } from '../../hooks/useConversationLogger'
import type { LiveKitParticipant } from '../../hooks/useLiveKit'
import { EndConversationDialog } from './EndConversationDialog'
import { ParticipantCircles } from './ParticipantCircles'
import { RecordingStartCountdown } from './RecordingStartCountdown'
import { RestartVoteDialog, type RestartVoteState } from './RestartVoteDialog'

export type RestartState = RestartVoteState

interface SessionCallProps {
  sessionId: string
  minDurationSec: number
  maxDurationSec: number
  callStartedAt?: string
  topic?: string
  instruction?: string
  onEnded: (durationSec: number) => void
  connectionState: ConnectionState
  participants: LiveKitParticipant[]
  participantInstructions?: Array<{ participantIndex: number; instructions: string }>
  isMuted: boolean
  toggleMute: () => void
  restart: RestartState
  restartCount: number
  restartPending: boolean
  onRequestRestart: () => void
  onVoteRestart: (vote: boolean) => void
  recordingStartsAt: string | null
  onRestartCountdownComplete: () => void
  topicCollapsed: boolean
  onTopicCollapsedChange: (collapsed: boolean) => void
  instructionsCollapsed: boolean
  onInstructionsCollapsedChange: (collapsed: boolean) => void
}

export function SessionCall({
  sessionId,
  minDurationSec,
  maxDurationSec,
  callStartedAt,
  topic,
  instruction,
  onEnded,
  connectionState,
  participants,
  participantInstructions,
  isMuted,
  toggleMute,
  restart,
  restartCount,
  restartPending,
  onRequestRestart,
  onVoteRestart,
  recordingStartsAt,
  onRestartCountdownComplete,
  topicCollapsed,
  onTopicCollapsedChange,
  instructionsCollapsed,
  onInstructionsCollapsedChange
}: SessionCallProps) {
  const { t } = useLingui()
  const log = useConversationLogger()

  const [elapsedSec, setElapsedSec] = useState(() =>
    callStartedAt ? Math.max(0, Math.floor((Date.now() - new Date(callStartedAt).getTime()) / 1000)) : 0
  )
  const [ending, setEnding] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const startTimeRef = useRef(callStartedAt ? new Date(callStartedAt).getTime() : Date.now())
  const wasConnectedRef = useRef(false)
  const autoEndTriggeredRef = useRef(false)
  const handleEndRef = useRef<() => void>(() => {})
  const elapsedSecRef = useRef(0)

  const [abandonedCountdown, setAbandonedCountdown] = useState<number | null>(null)
  const abandonedCountdownRef = useRef<number | null>(null)
  abandonedCountdownRef.current = abandonedCountdown
  const prevParticipantsCountRef = useRef(participants.length)

  const [restartCountdown, setRestartCountdown] = useState<number | null>(null)
  const restartCountdownCompleteRef = useRef(onRestartCountdownComplete)
  restartCountdownCompleteRef.current = onRestartCountdownComplete

  useEffect(() => {
    const prev = prevParticipantsCountRef.current
    prevParticipantsCountRef.current = participants.length

    if (!wasConnectedRef.current) return

    // Someone left and we're now alone — start the abandoned countdown
    if (participants.length < prev && participants.length < 2) {
      log('session', 'Participant left during call, starting abandoned countdown')
      setAbandonedCountdown(10)
      return
    }

    // Participant rejoined — cancel the countdown
    if (participants.length >= 2 && abandonedCountdownRef.current !== null) {
      log('session', 'Participant rejoined, cancelling abandoned countdown')
      setAbandonedCountdown(null)
    }
  }, [participants.length, log])

  useEffect(() => {
    if (abandonedCountdown === null) return
    if (abandonedCountdown <= 0) {
      log('session', 'Abandoned countdown reached zero, ending call')
      handleEndRef.current()
      return
    }
    const timer = setTimeout(() => setAbandonedCountdown((s) => (s !== null ? s - 1 : null)), 1000)
    return () => clearTimeout(timer)
  }, [abandonedCountdown, log])

  useEffect(() => {
    if (!recordingStartsAt) {
      setRestartCountdown(null)
      return
    }
    const target = new Date(recordingStartsAt).getTime()
    const tick = () => {
      const remaining = Math.ceil((target - Date.now()) / 1000)
      if (remaining <= 0) {
        setRestartCountdown(null)
        restartCountdownCompleteRef.current()
        return
      }
      setRestartCountdown(remaining)
    }
    tick()
    const interval = setInterval(tick, 200)
    return () => clearInterval(interval)
  }, [recordingStartsAt])

  const isRestartCountingDown = restartCountdown !== null && restartCountdown > 0

  useEffect(() => {
    log('info', 'SessionCall mounted', {
      sessionId,
      minDurationSec,
      maxDurationSec
    })
  }, [log, sessionId, minDurationSec, maxDurationSec])

  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      wasConnectedRef.current = true
    }
  }, [connectionState])

  useEffect(() => {
    if (restartCount === 0) return
    startTimeRef.current = Date.now()
    setElapsedSec(0)
    elapsedSecRef.current = 0
    autoEndTriggeredRef.current = false
    log('session', 'Timer reset for restart', { restartCount })
  }, [restartCount, log])

  // Sync startTimeRef when callStartedAt prop updates (e.g., after server sync)
  useEffect(() => {
    if (!callStartedAt) return
    const ts = new Date(callStartedAt).getTime()
    if (ts === startTimeRef.current) return
    // After a restart, the local timer resets at countdown completion while the
    // server sets callStartedAt 1-2s later. Skip small corrections (<5s) to
    // avoid a visible timer jump; only sync for large drifts.
    const drift = Math.abs(ts - startTimeRef.current)
    if (drift < 5000) return
    startTimeRef.current = ts
    const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000))
    elapsedSecRef.current = sec
    setElapsedSec(sec)
  }, [callStartedAt])

  useEffect(() => {
    const interval = setInterval(() => {
      const sec = Math.floor((Date.now() - startTimeRef.current) / 1000)
      elapsedSecRef.current = sec
      setElapsedSec(sec)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Detect room disconnection (other participant ended the session)
  useEffect(() => {
    if (wasConnectedRef.current && connectionState === ConnectionState.Disconnected && !ending) {
      log('session', 'Room disconnected (other participant ended session)', {
        elapsedSec: elapsedSecRef.current,
        connectionState
      })
      onEnded(elapsedSecRef.current)
    }
  }, [connectionState, ending, onEnded, log])

  useEffect(() => {
    if (elapsedSec >= maxDurationSec && !ending && !autoEndTriggeredRef.current) {
      autoEndTriggeredRef.current = true
      log('session', `Max duration reached (${maxDurationSec}s), auto-ending`)
      handleEndRef.current()
    }
  }, [elapsedSec, maxDurationSec, ending, log])

  const canEnd = elapsedSec >= minDurationSec
  const minReached = elapsedSec >= minDurationSec
  const endingCountdownSec = maxDurationSec > 0 ? maxDurationSec - elapsedSec : null
  const showEndingCountdown = endingCountdownSec !== null && endingCountdownSec <= 30 && endingCountdownSec > 0

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  const handleEnd = () => {
    log('api', `POST /v1/conversations/sessions/${sessionId}/leave`, {
      elapsedSec,
      minDurationSec
    })
    setEnding(true)
    setConfirmOpen(false)
    onEnded(elapsedSec)
    leaveSession(sessionId).catch((err) => {
      const resp = (err as { response?: { status?: number; data?: { message?: string } } }).response
      if (resp?.status !== 400) {
        log('error', `Leave API failed: ${resp?.data?.message || 'leave'}`)
      }
    })
  }
  handleEndRef.current = handleEnd

  if (connectionState === ConnectionState.Connecting) {
    return (
      <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
        <div className="flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
          <Spinner />
          <Text type="body2-regular" className="text-gray-90">
            <Trans>Connecting to room...</Trans>
          </Text>
        </div>
      </div>
    )
  }

  const showVoteModal = restart.activeRestartId !== null
  const minDurationLabel = formatTime(minDurationSec)
  const remainingLabel = formatTime(maxDurationSec - elapsedSec)
  const waitLabel = formatTime(minDurationSec - elapsedSec)
  const localParticipantIndex = participants.find((p) => p.isLocal)?.participantIndex
  const localParticipantInstruction =
    participantInstructions?.find((item) => item.participantIndex === localParticipantIndex)?.instructions ??
    participantInstructions?.[0]?.instructions

  return (
    <div
      style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
      className={clsx(
        'relative mx-auto flex w-full max-w-[800px] flex-col items-center gap-4',
        localParticipantInstruction && 'max-w-[1200px]'
      )}
    >
      {abandonedCountdown !== null && (
        <div className="bg-red/10 border-red/30 mt-4 flex w-full items-center justify-between rounded-xl border border-solid px-5 py-4">
          <div className="flex flex-col gap-0.5">
            <Text type="body2-regular" className="text-red font-medium">
              <Trans>Another participant left the conversation.</Trans>
            </Text>
            <Text type="body3-regular" className="text-red/80">
              <Trans>This conversation will end automatically in {abandonedCountdown}s.</Trans>
            </Text>
          </div>
          <span className="text-red ml-4 text-3xl font-bold tabular-nums">{abandonedCountdown}</span>
        </div>
      )}
      {showEndingCountdown && !isRestartCountingDown && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-black/70">
          <div className="flex flex-col items-center gap-3">
            <div className="bg-red flex size-24 items-center justify-center rounded-full">
              <span className="text-5xl font-bold text-white tabular-nums">{endingCountdownSec}</span>
            </div>
            <Text type="body2-regular" className="text-red font-medium">
              <Trans>Conversation ending...</Trans>
            </Text>
          </div>
        </div>
      )}
      <div className="flex w-full grow flex-col gap-4 rounded-2xl border border-solid border-gray-100 p-6 max-mobile:p-4">
        <button
          type="button"
          onClick={() => onTopicCollapsedChange(!topicCollapsed)}
          className="flex w-full cursor-default items-center justify-between gap-2 text-left max-mobile:cursor-pointer"
        >
          <span className="text-body-3 text-gray-90">&#8226; Topic to discuss</span>
          <ChevronDown
            className={clsx(
              'text-gray-80 hidden size-4 shrink-0 transition-transform max-mobile:block',
              topicCollapsed && '-rotate-90'
            )}
          />
        </button>
        <div className={clsx('flex flex-col gap-4', topicCollapsed && 'max-mobile:hidden')}>
          {topic && <div className="text-h5 leading-[110%] whitespace-pre-line">{topic}</div>}
          {instruction && <div className="text-gray-80 text-body2 mr-auto max-w-[800px]">{instruction}</div>}
        </div>
      </div>
      <div className="flex h-full w-full gap-4 max-mobile:flex-col max-mobile:w-full">
        {localParticipantInstruction && (
          <div className="text-body2 text-gray-80 mb-4 flex max-w-[300px] flex-col gap-4 rounded-2xl border border-solid border-gray-100 p-6 max-mobile:max-w-full max-mobile:w-full max-mobile:p-4">
            <button
              type="button"
              onClick={() => onInstructionsCollapsedChange(!instructionsCollapsed)}
              className="flex w-full cursor-default items-center justify-between gap-2 text-left max-mobile:cursor-pointer"
            >
              <span className="text-subtitle-semibold font-bold text-gray-70">
                <Trans> Participant Instructions</Trans>
              </span>
              <ChevronDown
                className={clsx(
                  'text-gray-80 hidden size-4 shrink-0 transition-transform max-mobile:block',
                  instructionsCollapsed && '-rotate-90'
                )}
              />
            </button>
            <div
              className={clsx('text-body2 text-gray-80', instructionsCollapsed && 'max-mobile:hidden')}
              dangerouslySetInnerHTML={{ __html: localParticipantInstruction }}
            />
          </div>
        )}
        <div className="flex w-full grow flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
          {isRestartCountingDown ? (
            <RecordingStartCountdown secondsRemaining={restartCountdown ?? 0} />
          ) : (
            <>
              <ParticipantCircles
                participants={participants}
                isMuted={isMuted}
                toggleMute={toggleMute}
                requiredCount={participants.length}
                readyCount={participants.length}
                showReadyStatus={false}
              />
              <div className="flex flex-col items-center gap-1">
                <span className="font-mono text-3xl font-semibold tabular-nums">{formatTime(elapsedSec)}</span>
                {restartCount > 0 && (
                  <Text type="body3-regular" className="text-gray-80">
                    <Plural value={restartCount} one="Restarted # time" other="Restarted # times" />
                  </Text>
                )}
                <Text type="body3-regular" className={minReached ? 'text-green' : 'text-gray-80'}>
                  {minReached ? t`Minimum duration reached` : t`Min: ${minDurationLabel}`}
                </Text>
                {maxDurationSec > 0 && (
                  <Text
                    type="body3-regular"
                    className={clsx(maxDurationSec - elapsedSec <= 60 ? 'text-red font-medium' : 'text-gray-80')}
                  >
                    {elapsedSec >= maxDurationSec ? t`Time's up` : t`${remainingLabel} remaining`}
                  </Text>
                )}
              </div>

              <div className="flex items-center gap-4">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={onRequestRestart}
                      disabled={restartPending || restart.activeRestartId !== null}
                      title={
                        restartPending || restart.activeRestartId !== null
                          ? t`A restart vote is in progress`
                          : t`Restart recording`
                      }
                      className={clsx(
                        'flex size-12 cursor-pointer items-center justify-center rounded-full border border-gray-100 transition-colors',
                        restartPending || restart.activeRestartId !== null
                          ? 'text-gray-80 cursor-not-allowed bg-gray-100 opacity-50'
                          : 'hover:bg-orange'
                      )}
                    >
                      <RefreshCcw className="size-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="text-body-2">
                    <Trans>Restart recording</Trans>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={toggleMute}
                      className={clsx(
                        'border-green text-green flex size-12 cursor-pointer items-center justify-center rounded-full border transition-colors',
                        isMuted ? 'text-red border-red hover:bg-red/20' : 'hover:bg-green/20'
                      )}
                    >
                      {isMuted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="text-body-2">
                    {isMuted ? <Trans>Unmute</Trans> : <Trans>Mute</Trans>}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setConfirmOpen(true)}
                      disabled={!canEnd || ending}
                      title={!canEnd ? t`Wait ${waitLabel} more` : t`End conversation`}
                      className={clsx(
                        'flex size-12 cursor-pointer items-center justify-center rounded-full border border-gray-100 transition-all',
                        canEnd && !ending
                          ? 'hover:bg-orange border-orange text-orange text-white hover:brightness-110 active:scale-95'
                          : 'text-gray-80 cursor-not-allowed bg-gray-100 opacity-50'
                      )}
                    >
                      <PhoneOff className="size-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="text-body-2">
                    <Trans>Finish conversation</Trans>
                  </TooltipContent>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      </div>
      <EndConversationDialog open={confirmOpen} onOpenChange={setConfirmOpen} onConfirmEnd={handleEnd} />
      <RestartVoteDialog
        open={showVoteModal}
        restart={restart}
        restartPending={restartPending}
        onVoteRestart={onVoteRestart}
      />
    </div>
  )
}
